var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { DEFAULT_CHORD_LENGTH, DEFAULT_PATTERN_LENGTH } from '../../constants';
import Sequencer from './Sequencer';
/**
 * A {@link Sequencer} used in the Simple2 (functions) block mode.
 */
var Simple2Sequencer = /** @class */ (function (_super) {
    __extends(Simple2Sequencer, _super);
    function Simple2Sequencer(library) {
        var _this = _super.call(this) || this;
        _this.library = library;
        _this.sequenceStack = [];
        _this.functionStack = [];
        _this.effectsStack = [];
        _this.randomStack = [];
        _this.functionMap = {};
        _this.uniqueInvocationIdUpTo = 0;
        _this.startMeasure = 1;
        _this.inTrigger = false;
        return _this;
    }
    /**
     * Resets to the default new sequence and clears all sequenced events
     */
    Simple2Sequencer.prototype.clear = function () {
        this.newSequence();
        this.functionMap = {};
    };
    Simple2Sequencer.prototype.getLastMeasure = function () {
        return this.getCurrentMeasure();
    };
    /**
     * Set up for a new sequence with a new set of blocks (e.g. at the start
     * of a trigger or when_run block)
     * @param startMeasure measure to start the sequence
     * @param inTrigger if the sequence is occurring in a trigger block
     */
    Simple2Sequencer.prototype.newSequence = function (startMeasure, inTrigger) {
        if (startMeasure === void 0) { startMeasure = 1; }
        if (inTrigger === void 0) { inTrigger = false; }
        this.resetStacks();
        this.startMeasure = startMeasure;
        this.inTrigger = inTrigger;
    };
    // Beginning of a play_sequential block.
    Simple2Sequencer.prototype.playSequential = function () {
        this.sequenceStack.push({
            measure: this.getCurrentMeasure(),
            together: false,
            lastMeasures: [],
        });
    };
    // End of a play_sequential block.
    Simple2Sequencer.prototype.endSequential = function () {
        this.endBlock(true);
    };
    // Beginning of a play_together block.
    Simple2Sequencer.prototype.playTogether = function () {
        var nextMeasure = this.getCurrentMeasure();
        this.sequenceStack.push({
            measure: nextMeasure,
            together: true,
            lastMeasures: [nextMeasure],
        });
    };
    // End of an play_together block.
    Simple2Sequencer.prototype.endTogether = function () {
        this.endBlock(false);
    };
    /**
     * Starts a new function context.
     */
    Simple2Sequencer.prototype.startFunctionContext = function (functionName) {
        var uniqueId = this.getUniqueInvocationId();
        this.functionMap[uniqueId] = {
            name: functionName,
            uniqueInvocationId: uniqueId,
            startMeasure: this.getCurrentMeasure(),
            endMeasure: this.getCurrentMeasure(),
            playbackEvents: [],
        };
        this.functionStack.push(uniqueId);
        var currentEffects = this.getCurrentEffects();
        if (currentEffects !== null) {
            // Create a fresh effect context if there is one active
            this.effectsStack.push({});
        }
    };
    /**
     * Ends the current function context.
     */
    Simple2Sequencer.prototype.endFunctionContext = function () {
        var lastFunctionId = this.functionStack.pop();
        if (lastFunctionId !== undefined) {
            this.functionMap[lastFunctionId].endMeasure = this.getCurrentMeasure();
        }
        this.effectsStack.pop();
    };
    /**
     * Start of a random block; the sequencer will randomly choose a sound from
     * within this block to be played.
     *
     * @param length
     * @param forceRandomIndex
     */
    Simple2Sequencer.prototype.startRandom = function (length, forceRandomIndex) {
        this.randomStack.push({
            skipSound: this.getCurrentSkipContext().skipSound,
            currentIndex: 0,
            randomIndex: forceRandomIndex !== undefined
                ? forceRandomIndex
                : Math.floor(Math.random() * length),
        });
    };
    // Move to the next child of a play_random block.
    Simple2Sequencer.prototype.nextRandom = function () {
        if (this.randomStack.length === 0) {
            console.warn('Invalid state; tried to call nextRandom() without active random context');
            return;
        }
        var currentEntry = this.randomStack[this.randomStack.length - 1];
        currentEntry.currentIndex++;
    };
    /**
     * Ends the current random block.
     */
    Simple2Sequencer.prototype.endRandom = function () {
        this.randomStack.pop();
    };
    Simple2Sequencer.prototype.setEffect = function (type, value) {
        var _a;
        var currentEffects = this.getCurrentEffects();
        if (currentEffects === null) {
            this.effectsStack.push((_a = {},
                _a[type] = value,
                _a));
        }
        else {
            currentEffects[type] = value;
        }
    };
    /**
     * Play a sound at the current location.
     */
    Simple2Sequencer.prototype.playSound = function (id, blockId) {
        var soundData = this.library.getSoundForId(id);
        if (soundData === null) {
            console.warn('Could not find sound with ID: ' + id);
            return;
        }
        this.addNewEvent(__assign({ id: id, type: 'sound', length: this.getLengthForId(id), soundType: soundData.type, blockId: blockId }, this.getCommonEventFields()));
    };
    /**
     * Play a pattern event at the current location.
     */
    Simple2Sequencer.prototype.playPattern = function (value, blockId) {
        this.addNewEvent(__assign({ type: 'pattern', id: JSON.stringify(value), value: value, blockId: blockId, length: DEFAULT_PATTERN_LENGTH }, this.getCommonEventFields()));
    };
    /**
     * Play a chord event at the current location.
     */
    Simple2Sequencer.prototype.playChord = function (value, blockId) {
        this.addNewEvent(__assign({ type: 'chord', id: JSON.stringify(value), value: value, length: DEFAULT_CHORD_LENGTH, blockId: blockId }, this.getCommonEventFields()));
    };
    Simple2Sequencer.prototype.rest = function (length) {
        this.updateMeasureForPlayByLength(length);
    };
    // Can be used to render timeline
    Simple2Sequencer.prototype.getOrderedFunctions = function () {
        var _this = this;
        return Object.keys(this.functionMap)
            .sort()
            .map(function (id) { return _this.functionMap[id]; });
    };
    Simple2Sequencer.prototype.getPlaybackEvents = function () {
        // Currently the Timeline still expects a list of PlaybackEvents, each with a reference
        // to their parent FunctionContext. We are reconstructing that model here.
        // Going forward, the Timeline could instead render using getOrderedFunctions(), and not have
        // to reconstruct the function mapping itself.
        return this.getOrderedFunctions()
            .map(function (functionEvent) {
            return functionEvent.playbackEvents.map(function (playbackEvent) {
                return __assign(__assign({}, playbackEvent), { functionContext: {
                        name: functionEvent.name,
                        uniqueInvocationId: functionEvent.uniqueInvocationId,
                    } });
            });
        })
            .flat();
    };
    Simple2Sequencer.prototype.getCommonEventFields = function () {
        return {
            triggered: this.inTrigger,
            when: this.getCurrentMeasure(),
            effects: __assign({}, this.getCurrentEffects()) || undefined,
            skipContext: this.getCurrentSkipContext(),
        };
    };
    Simple2Sequencer.prototype.addNewEvent = function (event) {
        var currentFunctionId = this.getCurrentFunctionId();
        if (currentFunctionId === null) {
            console.warn('Invalid state: no current function ID');
            return;
        }
        var currentFunction = this.functionMap[currentFunctionId];
        currentFunction.playbackEvents.push(event);
        this.updateMeasureForPlayByLength(event.length);
        currentFunction.endMeasure = this.getCurrentMeasure();
    };
    // Internal helper to get the entry at the top of the stack, or null
    // if the stack is empty.
    Simple2Sequencer.prototype.getCurrentSequenceFrame = function () {
        if (this.sequenceStack.length > 0) {
            return this.sequenceStack[this.sequenceStack.length - 1];
        }
        else {
            return null;
        }
    };
    Simple2Sequencer.prototype.getCurrentMeasure = function () {
        var currentEntry = this.getCurrentSequenceFrame();
        if (currentEntry === null) {
            return this.startMeasure;
        }
        return currentEntry.measure;
    };
    Simple2Sequencer.prototype.getCurrentFunctionId = function () {
        if (this.functionStack.length > 0) {
            return this.functionStack[this.functionStack.length - 1];
        }
        return null;
    };
    // Gets the current context for playing a sound, specifically whether
    // we are somewhere inside any play_random block, and whether we should
    // play this specific sound.
    Simple2Sequencer.prototype.getCurrentSkipContext = function () {
        if (this.randomStack.length > 0) {
            var currentEntry = this.randomStack[this.randomStack.length - 1];
            return {
                insideRandom: true,
                skipSound: currentEntry.skipSound ||
                    currentEntry.randomIndex !== currentEntry.currentIndex,
            };
        }
        else {
            return { insideRandom: false, skipSound: false };
        }
    };
    Simple2Sequencer.prototype.getCurrentEffects = function () {
        if (this.effectsStack.length > 0) {
            return this.effectsStack[this.effectsStack.length - 1];
        }
        return null;
    };
    // Internal function for the end of a play_sequential or play_together block.
    Simple2Sequencer.prototype.endBlock = function (isSequential) {
        var currentStackEntry = this.getCurrentSequenceFrame();
        if (currentStackEntry === null) {
            // warning - unexpeected
            return;
        }
        var nextMeasure = isSequential
            ? currentStackEntry.measure
            : Math.max.apply(Math, currentStackEntry.lastMeasures);
        // We are returning to the previous stack frame.
        this.sequenceStack.pop();
        var nextStackEntry = this.getCurrentSequenceFrame();
        if (nextStackEntry) {
            // Now the frame we are returning to has to absorb this information.
            if (nextStackEntry.together) {
                nextStackEntry.lastMeasures.push(nextMeasure);
            }
            else {
                nextStackEntry.measure = nextMeasure;
            }
        }
    };
    Simple2Sequencer.prototype.updateMeasureForPlayByLength = function (length) {
        var currentStackEntry = this.getCurrentSequenceFrame();
        if (currentStackEntry === null) {
            return;
        }
        if (currentStackEntry.together) {
            currentStackEntry.lastMeasures.push(currentStackEntry.measure + length);
        }
        else {
            currentStackEntry.measure += length;
        }
    };
    Simple2Sequencer.prototype.getUniqueInvocationId = function () {
        return this.uniqueInvocationIdUpTo++;
    };
    Simple2Sequencer.prototype.getLengthForId = function (id) {
        var soundData = this.library.getSoundForId(id);
        return soundData ? soundData.length : 0;
    };
    Simple2Sequencer.prototype.resetStacks = function () {
        this.sequenceStack = [];
        this.functionStack = [];
        this.effectsStack = [];
        this.randomStack = [];
    };
    return Simple2Sequencer;
}(Sequencer));
export default Simple2Sequencer;
